/// <reference types="@angular/localize" />

import { Component, computed, effect, inject, input, LOCALE_ID, OnInit, signal } from '@angular/core';
import { DmButton, DmInput } from '@digitale-menschen/component-library-angular';
import {
  authenticationControllerLoginV1,
  getLexplusAuthenticationRequestDtoInitialValue,
  lexplusAuthenticationRequestDtoSchema,
} from '@lexplus-authentication-api';
import { lastValueFrom } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { API_CONFIGURATION } from '@lexplus-environments';
import { Router, RouterLink } from '@angular/router';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import { AppDeviceService } from '../../services/appdevice.service';
import { AuthenticationService } from '../../services/authentication.service';
import { LocalDevAutoLoginService } from '../../services/local-dev-auto-login.service';
import { Store } from '@ngxs/store';
import {
  fieldErrorMessage,
  FormAlertComponent,
  LocalizedRouteService,
  normalizeLanguage,
  ProfileStateUpdatePasswordExpiration,
  TemporarySessionStateSetIsFirstLogin,
  TemporarySessionStateSetMustChangePassword,
  TemporarySessionStateSetPasswordChangeToken,
  TemporarySessionStateSetUserIdAndEmail,
} from '@lexplus-shared-lib';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from '../../auth-route-const';

@Component({
  selector: 'lp-login-form',
  imports: [DmButton, DmInput, RouterLink, FormField, FormRoot, FormAlertComponent],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
})
export class LexplusLoginFormComponent implements OnInit {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly localDevAutoLogin = inject(LocalDevAutoLoginService);
  private readonly router = inject(Router);
  private readonly appDeviceService = inject(AppDeviceService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly store = inject(Store);
  private readonly localizedRouteService = inject(LocalizedRouteService);
  private readonly localeId = inject(LOCALE_ID);

  public readonly isForAdmin = input<boolean>(false);

  private readonly loginModel = signal(getLexplusAuthenticationRequestDtoInitialValue());
  protected readonly loginForm = form(this.loginModel, lexplusAuthenticationRequestDtoSchema, {
    // `[formRoot]` in the template calls this on submit, but only once the schema's
    // validators pass, and never twice concurrently.
    submission: { action: () => this.login() },
  });

  protected readonly loadingStatus = signal<'loading' | 'success' | 'error' | null>(null);
  /** Server-side sign-in failure, shown as an alert inside the form rather than as a toast. */
  protected readonly loginErrorMessage = signal<string | null>(null);

  protected readonly emailErrorMessage = computed(() =>
    fieldErrorMessage(this.loginForm.email(), $localize`:@@auth.login.emailRequired:E-Mail-Adresse ist erforderlich.`),
  );
  protected readonly passwordErrorMessage = computed(() =>
    fieldErrorMessage(this.loginForm.password(), $localize`:@@auth.login.passwordRequired:Passwort ist erforderlich.`),
  );

  /** Editing either credential retracts the previous failure, so the alert never contradicts the input. */
  private readonly clearErrorOnCredentialEditEffect = effect(() => {
    this.loginForm.email().value();
    this.loginForm.password().value();
    this.loginErrorMessage.set(null);
  });

  protected readonly changePasswordRoute = `/${this.localizedRouteService.getLocalizedRoute(
    LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT],
    LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.CHANGE_PASSWORD],
  )}`;

  protected readonly contactUrl = `https://www.neosys.ch/kontakt/?lang=${normalizeLanguage(this.localeId) ?? 'de'}`;

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  public async ngOnInit(): Promise<void> {
    await this.applyLocalDevCredentials();
  }

  /**
   * Handles the login workflow for a user, including sending authentication requests
   * and navigation to further authentication steps if necessary.
   */
  public async login(): Promise<void> {
    this.loadingStatus.set('loading');
    // Cleared up front so a repeated failure re-mounts the alert, which both
    // re-announces it and makes the retry visible.
    this.loginErrorMessage.set(null);

    try {
      this.loginForm.appDeviceId?.().value.set(this.appDeviceService.getDeviceId() || '');

      const result = await lastValueFrom(
        authenticationControllerLoginV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: { ...this.loginForm().value(), forAdmin: this.isForAdmin() },
        }),
      );

      this.store.dispatch(
        new ProfileStateUpdatePasswordExpiration(result.body.showPasswordExpireWarning, result.body.passwordExpirationDate),
      );

      if (result.body.requires2fa) {
        this.store.dispatch(new TemporarySessionStateSetUserIdAndEmail(<string>result.body.userId, this.loginForm.email().value()));
        this.store.dispatch(new TemporarySessionStateSetIsFirstLogin(result.body.isFirstLogin));
        this.store.dispatch(new TemporarySessionStateSetMustChangePassword(result.body.mustChangePassword));
        this.store.dispatch(new TemporarySessionStateSetPasswordChangeToken(result.body.passwordChangeToken ?? null));
        await this.router.navigateByUrl(
          `/${this.localizedRouteService.getLocalizedRoute(LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT], LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.TWO_FACTOR])}`,
        );
        return;
      }

      await this.authenticationService.routeAndProcessAfterLogin(
        result.body.mustChangePassword,
        result.body.isFirstLogin,
        result.body.passwordChangeToken ?? null,
        this.loginForm.email().value(),
      );
    } catch (loginFailedException) {
      this.loadingStatus.set('error');
      const serverMessage = (loginFailedException as HttpErrorResponse)?.error?.message;
      this.loginErrorMessage.set(
        serverMessage ?? $localize`:@@auth.login.failedDescription:Bitte die Eingaben überprüfen und erneut versuchen.`,
      );
    }
  }

  /**
   * Signs in with the credentials from the gitignored `dev-credentials.json` in the app's
   * `public/` folder, on a localhost dev server only. Does nothing when the file is absent,
   * unparsable or incomplete, so every other environment and every checkout without the
   * file keeps the normal login.
   *
   * The folder is declared as a bare-string asset in `project.json`, so it is served under
   * `/public/` rather than at the web root.
   *
   * LocalDevAutoLoginService decides whether it may run at all — an explicit logout switches it
   * off for this dev server so a different account can be entered by hand.
   */
  private async applyLocalDevCredentials(): Promise<void> {
    if (!this.localDevAutoLogin.shouldSignIn()) {
      return;
    }

    let credentials: { email?: string; password?: string };
    try {
      const response = await fetch('/public/dev-credentials.json');
      if (!response.ok) {
        return;
      }
      credentials = (await response.json()) as { email?: string; password?: string };
    } catch {
      return;
    }

    if (!credentials.email || !credentials.password) {
      return;
    }

    this.localDevAutoLogin.markAttempted();
    this.loginForm.email().value.set(credentials.email);
    this.loginForm.password().value.set(credentials.password);
    await this.login();
  }
}
